import React from 'react';
import Layout from '../components/layout/Layout';
import { GraduationCap, Package, Monitor, Settings, Building, Lightbulb, Code, Check, ArrowRight } from 'lucide-react';
import { offerings } from '../data/mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const iconMap = {
  GraduationCap,
  Package,
  Monitor,
  Settings,
  Building,
  Lightbulb,
  Code
};

const OfferingsPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-white/10 text-blue-300 text-sm font-semibold rounded-full mb-4 md:mb-6 backdrop-blur-sm border border-white/20">
            What We Offer
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Comprehensive STEAM Solutions
          </h1>
          <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            From K-12 curriculum to enterprise solutions, we provide everything needed 
            to implement world-class STEAM education.
          </p>
        </div>
      </section>

      {/* Offerings Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 md:space-y-24">
            {offerings.map((offering, index) => {
              const IconComponent = iconMap[offering.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={offering.id}
                  className={`grid lg:grid-cols-2 gap-8 md:gap-16 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}
                >
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg">
                      <IconComponent className="w-6 h-6 md:w-8 md:h-8 text-white" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">{offering.title}</h2>
                    <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">{offering.description}</p>
                    
                    <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                      {offering.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="w-5 h-5 md:w-6 md:h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
                          </div>
                          <span className="text-gray-700 text-sm md:text-base">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to="/contact">
                      <Button className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white px-6 md:px-8 py-2 md:py-3 rounded-full group">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>

                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <div className="relative">
                      <div className="bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
                        {offering.image ? (
                          <img 
                            src={offering.image} 
                            alt={offering.title}
                            className="w-full h-[250px] md:h-[400px] object-cover hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="p-6 md:p-8 aspect-square flex items-center justify-center">
                            <div className="text-center">
                              <IconComponent className="w-24 h-24 md:w-32 md:h-32 text-blue-600/30 mx-auto mb-4" />
                              <h3 className="text-xl md:text-2xl font-bold text-gray-800">{offering.title}</h3>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-4 -right-4 w-16 h-16 md:w-24 md:h-24 bg-orange-400/20 rounded-full blur-xl"></div>
                      <div className="absolute -top-4 -left-4 w-20 h-20 md:w-32 md:h-32 bg-blue-400/20 rounded-full blur-xl"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 to-teal-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            Ready to Transform Your Institution?
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8 px-4">
            Get in touch with our team to discuss how STEAM-IE can help you implement 
            world-class STEAM education.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 md:px-10 py-4 md:py-6 text-base md:text-lg rounded-full font-semibold">
              Contact Us Today
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default OfferingsPage;
